import type { ComicCharacterCard } from './comicCharacters'
import { classifySpeechField } from './comicPanelDialogue'
import type { ComicPanel, ComicPanelCharacterLine } from './comicTypes'

const MAX_LINE = 500

function stripTerminalFullStop(raw: string): string {
  let s = raw.trim()
  while (s.endsWith('。') || s.endsWith('.')) {
    s = s.slice(0, -1).trim()
  }
  return s
}

function clip(raw: string): string {
  const s = stripTerminalFullStop(raw)
  return s.length > MAX_LINE ? s.slice(0, MAX_LINE) : s
}

function lineFromRaw(o: Record<string, unknown>): ComicPanelCharacterLine {
  const characterId = typeof o.characterId === 'string' ? o.characterId.trim() : ''
  const legacyDialogue = typeof o.dialogue === 'string' ? o.dialogue : ''
  let speech = typeof o.speech === 'string' ? clip(o.speech) : ''
  let thought = typeof o.thought === 'string' ? clip(o.thought) : ''
  if (!speech && !thought && legacyDialogue.trim()) {
    const { kind, text } = classifySpeechField(legacyDialogue)
    if (kind === 'thought') thought = clip(text)
    else speech = clip(text)
  }
  return { characterId, speech, thought }
}

export function normalizePanelCharacterLines(raw: unknown): ComicPanelCharacterLine[] {
  if (!Array.isArray(raw)) return []
  const out: ComicPanelCharacterLine[] = []
  const seen = new Set<string>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const line = lineFromRaw(item as Record<string, unknown>)
    if (!line.characterId || seen.has(line.characterId)) continue
    seen.add(line.characterId)
    out.push(line)
    if (out.length >= 12) break
  }
  return out
}

export function serializeCharacterLinesToDialogue(
  lines: ComicPanelCharacterLine[],
  characters: ComicCharacterCard[],
): string {
  const nameById = new Map(characters.map((c) => [c.id, c.name.trim() || '角色']))
  const chunks: string[] = []
  for (const l of lines) {
    const name = nameById.get(l.characterId) ?? '角色'
    if (l.speech.trim()) chunks.push(`${name}：${l.speech.trim()}`)
    if (l.thought.trim()) chunks.push(`${name}（心声）：${l.thought.trim()}`)
  }
  return chunks.join('\n')
}

export function hydratePanelCharacterLines(panel: ComicPanel): ComicPanelCharacterLine[] {
  const stored = normalizePanelCharacterLines(panel.characterLines)
  const ids = panel.characterIds ?? []
  if (stored.length > 0) {
    const idSet = new Set(ids)
    return stored.filter((l) => idSet.has(l.characterId))
  }
  if (ids.length === 0) return []
  const dialogue = panel.dialogue.trim()
  if (dialogue && ids.length === 1) {
    const { kind, text } = classifySpeechField(dialogue)
    return [
      {
        characterId: ids[0]!,
        speech: kind === 'speech' ? clip(text) : '',
        thought: kind === 'thought' ? clip(text) : '',
      },
    ]
  }
  return ids.map((characterId) => ({ characterId, speech: '', thought: '' }))
}

export function syncLinesWithCharacterIds(
  ids: string[],
  prev: ComicPanelCharacterLine[],
): ComicPanelCharacterLine[] {
  const byId = new Map(prev.map((l) => [l.characterId, l]))
  return ids.map(
    (characterId) => byId.get(characterId) ?? { characterId, speech: '', thought: '' },
  )
}

export function characterLinePreview(line: ComicPanelCharacterLine): string {
  const parts: string[] = []
  if (line.speech.trim()) parts.push(`台词：${line.speech.trim()}`)
  if (line.thought.trim()) parts.push(`心声：${line.thought.trim()}`)
  return parts.join(' · ') || '（暂无台词）'
}

export function panelHasCharacterSpeechContent(
  panel: ComicPanel,
  lines?: ComicPanelCharacterLine[],
): boolean {
  const normalized = lines ?? normalizePanelCharacterLines(panel.characterLines)
  if (normalized.some((l) => l.speech.trim() || l.thought.trim())) return true
  return Boolean(panel.dialogue.trim())
}
